import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { adminScopeApi } from '@/api/admin-scope.api';
import { useApp } from '@/hooks/use-app-context';
import { useLanguages } from '@/hooks/use-languages';
import type { UserRole } from '@/types/auth.types';

const normalizeRole = (value: unknown): UserRole => {
  if (value === 'admin' || value === 'collector' || value === 'moderator' || value === 'user' || value === 'accountant' || value === 'super_admin') {
    return value;
  }
  if (value === 'super_admin' || value === 'SUPER_ADMIN') return 'super_admin';
  if (value === 'ADMIN') return 'admin';
  if (value === 'COLLECTOR' || value === 'COLLECTEUR' || value === 'collecteur' || value === 'ANNOTATOR' || value === 'ANNOTATEUR') return 'collector';
  if (value === 'MODERATOR') return 'moderator';
  if (value === 'ACCOUNTANT' || value === 'COMPTABLE') return 'accountant';
  return 'user';
};

/**
 * A language is stored either as its bare code (`moba`) or prefixed by its
 * country (`tg moba`). The scope accepts both forms, so both are compared.
 */
const codeAliases = (value: string | undefined | null): string[] => {
  const normalized = normalizeCode(value);
  if (!normalized) return [];
  const parts = normalized.split(/\s+/).filter((part) => part.length > 0);
  const last = parts[parts.length - 1];
  return last && last !== normalized ? [normalized, last] : [normalized];
};

const normalizeCode = (value: string | undefined | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : undefined;
};

/**
 * What the admin space knows about the languages the caller may read.
 *
 * - `loading`: the scope is being read, the answer is not known yet.
 * - `error`: the scope could not be read. The screens must say so, and
 *   offer a retry, instead of showing an empty list as if there were nothing
 *   to read.
 * - `empty`: the scope is known and holds no language. The administrator has
 *   no language in charge, and the admin views stay empty until one is
 *   assigned.
 * - `ready`: the scope is known and holds at least one language.
 */
export type AdminScopeState = 'loading' | 'error' | 'empty' | 'ready';

export function useAdminScope() {
  const { user } = useApp();

  const normalizedRole = normalizeRole(user?.role);
  const isSuperAdmin = normalizedRole === 'super_admin';
  const { languages, isLoading, error, refetch } = useLanguages({
    scope: 'all',
    enabled: Boolean(user?.id),
  });

  // The server is the authority: it refuses a language outside the scope with
  // 403 LANGUAGE_OUT_OF_SCOPE. The filters only offer what it allows.
  const isAdminRole = normalizedRole === 'admin' || isSuperAdmin;
  const scopeQuery = useQuery({
    queryKey: ['admin-language-scope', user?.id],
    queryFn: ({ signal }) => adminScopeApi.getLanguageScope(signal),
    enabled: Boolean(user?.id) && isAdminRole,
    staleTime: 60_000,
    retry: 1,
  });

  // Fails closed: while an admin's scope is loading or failed to load, no
  // language is offered rather than every language.
  const isScopedAdmin = normalizedRole === 'admin';
  const scopedCodes = useMemo(() => {
    const scope = scopeQuery.data;
    if (!scope) return isScopedAdmin ? new Set<string>() : null;
    if (scope.all) return null;
    return new Set(scope.languages.flatMap((code) => codeAliases(code)));
  }, [isScopedAdmin, scopeQuery.data]);

  const accessibleLanguages = useMemo(() => {
    if (!scopedCodes) return languages;
    return languages.filter((language) =>
      codeAliases(language.code).some((alias) => scopedCodes.has(alias)),
    );
  }, [languages, scopedCodes]);

  const accessibleLanguageCodes = useMemo(() => {
    return new Set(
      accessibleLanguages
        .map((language) => normalizeCode(language.code))
        .filter((languageCode): languageCode is string => Boolean(languageCode))
    );
  }, [accessibleLanguages]);

  const canAccessLanguage = useCallback(
    (languageCode?: string | null) => {
      const normalized = normalizeCode(languageCode);
      if (!normalized) {
        return isSuperAdmin;
      }

      if (isSuperAdmin) return true;
      return codeAliases(normalized).some((alias) =>
        accessibleLanguageCodes.has(alias),
      );
    },
    [accessibleLanguageCodes, isSuperAdmin]
  );

  const getScopedLanguage = useCallback(
    (selectedLanguage?: string | null) => {
      if (isSuperAdmin) {
        return selectedLanguage ?? undefined;
      }

      if (canAccessLanguage(selectedLanguage)) {
        return selectedLanguage ?? accessibleLanguages[0]?.code;
      }

      return accessibleLanguages[0]?.code;
    },
    [accessibleLanguages, canAccessLanguage, isSuperAdmin]
  );

  const filterByScope = useCallback(
    <T,>(items: T[], readLanguage: (item: T) => string | undefined | null) => {
      if (isSuperAdmin) {
        return items;
      }

      return items.filter((item) => canAccessLanguage(readLanguage(item)));
    },
    [canAccessLanguage, isSuperAdmin]
  );

  // What the admin space knows about the scope, so the screens can tell an
  // empty scope, a scope that could not be read, and a scope that is still
  // being read apart. A super admin reads every language and never depends
  // on the scope.
  const scopeState = useMemo<AdminScopeState>(() => {
    if (isSuperAdmin) return 'ready';
    if (!isAdminRole) return 'ready';
    if (scopeQuery.isLoading) return 'loading';
    if (scopeQuery.isError) return 'error';
    if (!scopeQuery.data) return 'loading';
    if (scopeQuery.data.all) return 'ready';
    return scopeQuery.data.languages.length > 0 ? 'ready' : 'empty';
  }, [
    isAdminRole,
    isSuperAdmin,
    scopeQuery.data,
    scopeQuery.isError,
    scopeQuery.isLoading,
  ]);

  return {
    isLoading: isLoading || scopeQuery.isLoading,
    error,
    refetch,
    scopeState,
    scopeError: scopeQuery.error,
    refetchScope: scopeQuery.refetch,
    isSuperAdmin,
    allLanguages: languages,
    visibleLanguages: isSuperAdmin ? languages : accessibleLanguages,
    accessibleLanguages,
    accessibleLanguageCodes,
    hasAccessibleLanguages: isSuperAdmin || accessibleLanguages.length > 0,
    canAccessLanguage,
    getScopedLanguage,
    filterByScope,
  };
}